import React, { useState, useEffect, useCallback, useRef } from "react";
import { FaPlus, FaMinus } from "react-icons/fa";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { MdClose } from "react-icons/md";
// import { MdLogin, MdOutlineSpaceDashboard } from "react-icons/md";
import { IoMdMenu } from "react-icons/io";
// import { IoMdLogOut } from "react-icons/io";
// import { GiRank3 } from "react-icons/gi";

const Nav = () => {
  const location = useLocation();

  const [nav, setNav] = useState(false);
  const [dropdown, setDropdown] = useState({});
  // Same first render at build time and in the browser; measure after mount.
  const [isMobile, setIsMobile] = useState(false);

  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const firstMobileLinkRef = useRef(null);

  const mobileTopLinkClass =
    "block text-lg font-display uppercase tracking-[0.14em] text-mmText hover:text-mmAccent py-3 border-b border-mmBorder focus-visible:ring-2 focus-visible:ring-mmFocus";
  const mobileCtaLinkClass =
    "block rounded-lg border border-mmAccent/60 bg-mmAccent/10 px-4 py-3 text-lg font-display uppercase tracking-[0.14em] text-mmAccent hover:bg-mmAccent/15 focus-visible:ring-2 focus-visible:ring-mmFocus";
  const mobileAccordionLabelClass =
    "flex justify-between items-center text-mmText hover:text-mmAccent uppercase text-lg font-display tracking-[0.14em] py-3 border-b border-mmBorder cursor-pointer";
  const mobileSubmenuClass =
    "block text-sm text-mmTextMuted hover:text-mmAccent py-1 first:pt-2 last:pb-2 focus-visible:ring-2 focus-visible:ring-mmFocus";
  const desktopLinkClass =
    "text-mmText hover:text-mmAccent text-2xl font-display uppercase tracking-[0.14em]";
  const desktopCtaLinkClass =
    "inline-flex rounded-lg border border-mmAccent/60 bg-mmAccent/10 px-4 py-3 text-2xl font-display uppercase tracking-[0.14em] text-mmAccent hover:bg-mmAccent/15";
  const desktopGroupLabelClass =
    "text-mmTextMuted text-xl font-display uppercase tracking-[0.18em] cursor-default select-none";
  const desktopSubmenuLinkClass =
    "block text-mmTextMuted hover:text-mmAccent py-1";

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleDropdown = (section) => {
    setDropdown((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Hide/show navbar on scroll (keep your premium feel)
  const [visible, setVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;

    if (currentScrollY === 0) {
      setVisible(true);
      return;
    }

    if (currentScrollY > lastScrollY + 10) {
      setVisible(false);
    } else if (currentScrollY < lastScrollY - 10) {
      setVisible(true);
    }

    setLastScrollY(currentScrollY);
  }, [lastScrollY]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Scroll lock behind menu
  useEffect(() => {
    document.body.style.overflow = nav ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [nav]);

  // Escape closes menu
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setNav(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setNav(false);
  }, [location.pathname]);

  // Focus first link when menu opens (mobile)
  useEffect(() => {
    if (nav && isMobile && firstMobileLinkRef.current) {
      firstMobileLinkRef.current.focus();
    }
  }, [nav, isMobile]);

  // Focus trap when menu open
  useEffect(() => {
    const menuEl = menuRef.current;
    if (!menuEl) return;

    const getFocusable = () =>
      Array.from(
        menuEl.querySelectorAll(
          'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter(
        (el) => !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden"),
      );

    if (nav) {
      const focusables = getFocusable();
      if (focusables.length > 0) focusables[0].focus();

      const handleTab = (event) => {
        if (event.key !== "Tab") return;
        const items = getFocusable();
        if (items.length === 0) return;

        const firstEl = items[0];
        const lastEl = items[items.length - 1];

        if (event.shiftKey && document.activeElement === firstEl) {
          event.preventDefault();
          lastEl.focus();
        } else if (!event.shiftKey && document.activeElement === lastEl) {
          event.preventDefault();
          firstEl.focus();
        }
      };

      window.addEventListener("keydown", handleTab);
      return () => window.removeEventListener("keydown", handleTab);
    }

    // Return focus to hamburger when closing
    if (!nav && menuButtonRef.current) {
      menuButtonRef.current.focus();
    }
  }, [nav]);

  return (
    <div
      className={`fixed top-0 w-screen px-6 h-16 md:h-20 bg-bg backdrop-blur border-b border-mmAccent z-50 flex justify-between items-center text-mmText text-lg transition-transform duration-300 ${
        visible ? "translate-y-0" : "-translate-y-full"
      }`}
    >
      {isMobile ? (
        <>
          {/* Mobile Top Bar */}
          <div className="w-full grid grid-cols-[56px,1fr,56px] items-center">
            {/* Left: Hamburger */}
            <div className="w-[56px] flex items-center justify-start">
              <button
                type="button"
                className="flex items-center justify-center text-mmText focus-visible:ring-2 focus-visible:ring-mmFocus rounded"
                onClick={() => setNav(!nav)}
                aria-label={nav ? "Close menu" : "Open menu"}
                aria-expanded={nav}
                aria-controls="main-menu"
                ref={menuButtonRef}
              >
                {nav ? (
                  <MdClose size={32} className="text-mmText" />
                ) : (
                  <IoMdMenu size={32} className="text-mmText" />
                )}
              </button>
            </div>

            {/* Center: Brand */}
            <RouterLink
              to="/"
              aria-label="Ga naar homepage"
              className="flex flex-col items-center justify-self-center text-center text-sm font-display uppercase tracking-[0.14em] text-mmAccent"
              onClick={() => setNav(false)}
            >
              <span>Mission</span>
              <span>Movement</span>
            </RouterLink>

            {/* Right: Login/Dashboard icon (mirrored position) */}
            <div className="w-[56px] flex items-center justify-end">
              {/* TEMP: Auth icons hidden until back office is live */}
              {/*
              {!user ? (
                <RouterLink
                  to="/login"
                  alt="to-login-page"
                  className="flex items-center justify-center focus-visible:ring-2 focus-visible:ring-mmFocus rounded"
                >
                  <MdLogin
                    size={28}
                    className="text-mmText hover:text-mmAccent"
                  />
                </RouterLink>
              ) : (
                <RouterLink
                  to="/dashboard"
                  alt="to-dashboard"
                  className="flex items-center justify-center focus-visible:ring-2 focus-visible:ring-mmFocus rounded"
                >
                  <GiRank3 size={28} className="text-mmAccent" />
                </RouterLink>
              )}
              */}
              <div className="w-[28px] h-[28px]" aria-hidden="true" />
            </div>
          </div>

          {/* Mobile Menu Panel */}
          <div
            className={`absolute top-full left-0 w-full bg-bg border-t border-mmBorder overflow-y-auto h-[calc(100dvh-4rem)] transition-transform transition-opacity duration-500 ease-out transform-gpu will-change-transform ${
              nav
                ? "translate-y-0 opacity-100 pointer-events-auto"
                : "-translate-y-3 opacity-0 pointer-events-none"
            }`}
            style={{ fontFamily: "Overpass, sans-serif" }}
            id="main-menu"
            role="dialog"
            aria-modal="true"
            ref={menuRef}
          >
            <form className="px-6 py-8 text-mmText">
              <div className="mb-4">
                <RouterLink
                  to="/pricing"
                  ref={firstMobileLinkRef}
                  onClick={() => setNav(false)}
                  className={mobileCtaLinkClass}
                >
                  Start je voorbereiding
                </RouterLink>
              </div>

              {/* Defensie expandable */}
              <div>
                <input
                  type="checkbox"
                  id="menu-Defensie"
                  className="hidden"
                  checked={dropdown["Defensie"] || false}
                  onChange={() => toggleDropdown("Defensie")}
                />
                <label
                  htmlFor="menu-Defensie"
                  className={mobileAccordionLabelClass}
                >
                  Defensie
                  <span>
                    {dropdown["Defensie"] ? (
                      <FaMinus size={16} />
                    ) : (
                      <FaPlus size={16} />
                    )}
                  </span>
                </label>

                <ul
                  className={`pl-4 overflow-hidden transition-all duration-300 ease-in-out ${
                    dropdown["Defensie"]
                      ? "max-h-96 opacity-100"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  {[
                    {
                      title: "Voorbereiding Defensie",
                      path: "/voorbereiding-defensie",
                    },
                    {
                      title: "Cluster 6 Vrouwen",
                      path: "/cluster-6-voorbereiding-vrouwen",
                    },
                    { title: "Korps Mariniers", path: "/units/mariniers" },
                    {
                      title: "Korps Commando Troepen",
                      path: "/units/commandotroepen",
                    },
                    {
                      title: "11 Luchtmobiele Brigade",
                      path: "/units/luchtmobiel",
                    },
                  ].map(({ title, path }) => (
                    <RouterLink
                      key={title}
                      to={path}
                      onClick={() => setNav(false)}
                      className={mobileSubmenuClass}
                    >
                      {title}
                    </RouterLink>
                  ))}
                </ul>
              </div>

              {/* Veiligheidsdiensten: directe link (geen dropdown nodig) */}
              <RouterLink
                to="/voorbereiding-veiligheidsdiensten"
                onClick={() => setNav(false)}
                className={mobileTopLinkClass}
              >
                Veiligheidsdiensten
              </RouterLink>

              {/* Programma's expandable */}
              <div>
                <input
                  type="checkbox"
                  id="menu-Programmas"
                  className="hidden"
                  checked={dropdown["Programmas"] || false}
                  onChange={() => toggleDropdown("Programmas")}
                />
                <label
                  htmlFor="menu-Programmas"
                  className={mobileAccordionLabelClass}
                >
                  Programma’s
                  <span>
                    {dropdown["Programmas"] ? (
                      <FaMinus size={16} />
                    ) : (
                      <FaPlus size={16} />
                    )}
                  </span>
                </label>

                <ul
                  className={`pl-4 overflow-hidden transition-all duration-300 ease-in-out ${
                    dropdown["Programmas"]
                      ? "max-h-56 opacity-100"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  {[
                    { title: "Defensie-programma", path: "/program" },
                    {
                      title: "Cluster 6 Vrouwen",
                      path: "/cluster-6-voorbereiding-vrouwen",
                    },
                    {
                      title: "Programma Veiligheidsdiensten",
                      path: "/programma-veiligheidsdiensten",
                    },
                    { title: "Aanbod & prijzen", path: "/pricing" },
                  ].map(({ title, path }) => (
                    <RouterLink
                      key={title}
                      to={path}
                      onClick={() => setNav(false)}
                      className={mobileSubmenuClass}
                    >
                      {title}
                    </RouterLink>
                  ))}
                </ul>
              </div>

              {/* Kennisbank expandable */}
              <div>
                <input
                  type="checkbox"
                  id="menu-Kennisbank"
                  className="hidden"
                  checked={dropdown["Kennisbank"] || false}
                  onChange={() => toggleDropdown("Kennisbank")}
                />
                <label
                  htmlFor="menu-Kennisbank"
                  className={mobileAccordionLabelClass}
                >
                  Kennisbank
                  <span>
                    {dropdown["Kennisbank"] ? (
                      <FaMinus size={16} />
                    ) : (
                      <FaPlus size={16} />
                    )}
                  </span>
                </label>

                <ul
                  className={`pl-4 overflow-hidden transition-all duration-300 ease-in-out ${
                    dropdown["Kennisbank"]
                      ? "max-h-28 opacity-100"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  {[
                    { title: "Artikelen", path: "/blogs" },
                    { title: "Bronnen", path: "/resources" },
                  ].map(({ title, path }) => (
                    <RouterLink
                      key={title}
                      to={path}
                      onClick={() => setNav(false)}
                      className={mobileSubmenuClass}
                    >
                      {title}
                    </RouterLink>
                  ))}
                </ul>
              </div>

              {[
                { title: "Ervaringen", path: "/ervaringen" },
                { title: "Over ons", path: "/about" },
                { title: "Contact", path: "/contact" },
              ].map(({ title, path }) => (
                <RouterLink
                  key={title}
                  to={path}
                  onClick={() => setNav(false)}
                  className={mobileTopLinkClass}
                >
                  {title}
                </RouterLink>
              ))}
            </form>
          </div>
        </>
      ) : (
        <>
          {/* Desktop Left: Menu button */}
          <button
            type="button"
            className="flex items-center cursor-pointer text-mmText outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
            onClick={() => setNav(!nav)}
            aria-expanded={nav}
            aria-controls="main-menu"
            ref={menuButtonRef}
          >
            {nav ? <MdClose size={35} /> : <IoMdMenu size={35} />}
            <span className="ml-2 text-lg font-display uppercase tracking-[0.14em] hidden lg:inline">
              Menu
            </span>
          </button>

          {/* Desktop Center: Brand */}
          <RouterLink
            to="/"
            className="text-xl font-display uppercase tracking-[0.14em] text-mmAccent"
            onClick={() => setNav(false)}
          >
            <span>Mission</span>
            <span className="block text-center">Movement</span>
          </RouterLink>

          {/* Desktop quick links: zichtbaar in de balk zonder menu te openen */}
          <nav className="flex items-center gap-5 lg:gap-6" aria-label="Snelle navigatie">
            <RouterLink
              to="/voorbereiding-check"
              onClick={() => setNav(false)}
              className="text-sm lg:text-base font-display uppercase tracking-[0.14em] text-mmTextMuted hover:text-mmAccent transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-mmFocus rounded"
            >
              Gratis check
            </RouterLink>
            <RouterLink
              to="/pricing"
              onClick={() => setNav(false)}
              className="inline-flex items-center rounded-lg border border-mmAccent/60 bg-mmAccent/10 px-3.5 py-2 text-sm lg:text-base font-display uppercase tracking-[0.14em] text-mmAccent hover:bg-mmAccent/20 transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-mmFocus"
            >
              Start je voorbereiding
            </RouterLink>
          </nav>

          {/* Desktop Dropdown */}
          <div
            className={`absolute top-full left-0 w-full bg-bg border-t border-mmBorder shadow-lg transition-all duration-300 ${
              nav ? "h-[80vh] opacity-100" : "h-0 opacity-0 pointer-events-none"
            } overflow-y-auto overflow-x-hidden`}
            id="main-menu"
            role="dialog"
            aria-modal="true"
            ref={menuRef}
          >
            <div className="grid grid-cols-3 gap-8 px-20 mt-4 pb-4">
              {/* Column 1 */}
              <div className="flex flex-col space-y-8 pt-4">
                <div className="pb-2">
                  <RouterLink
                    to="/pricing"
                    className={desktopCtaLinkClass}
                    onClick={() => setNav(false)}
                  >
                    Start je voorbereiding
                  </RouterLink>
                </div>

                <div>
                  <h3 className={desktopGroupLabelClass}>Defensie</h3>
                  <div className="border-t border-mmBorder w-48 my-2" />
                  <RouterLink
                    to="/voorbereiding-defensie"
                    className={desktopSubmenuLinkClass}
                    onClick={() => setNav(false)}
                  >
                    Voorbereiding Defensie
                  </RouterLink>
                  <RouterLink
                    to="/cluster-6-voorbereiding-vrouwen"
                    className={desktopSubmenuLinkClass}
                    onClick={() => setNav(false)}
                  >
                    Cluster 6 Vrouwen
                  </RouterLink>
                  <RouterLink
                    to="/units/mariniers"
                    className={desktopSubmenuLinkClass}
                    onClick={() => setNav(false)}
                  >
                    Korps Mariniers
                  </RouterLink>
                  <RouterLink
                    to="/units/commandotroepen"
                    className={desktopSubmenuLinkClass}
                    onClick={() => setNav(false)}
                  >
                    Korps Commando Troepen
                  </RouterLink>
                  <RouterLink
                    to="/units/luchtmobiel"
                    className={desktopSubmenuLinkClass}
                    onClick={() => setNav(false)}
                  >
                    11 Luchtmobiele brigade
                  </RouterLink>
                </div>

                <div>
                  <RouterLink
                    to="/voorbereiding-veiligheidsdiensten"
                    className={desktopLinkClass}
                    onClick={() => setNav(false)}
                  >
                    Veiligheidsdiensten
                  </RouterLink>
                  <div className="border-t border-mmBorder w-48 my-2" />
                </div>
              </div>

              {/* Column 2 */}
              <div className="flex flex-col space-y-8 pt-4">
                <div>
                  <h3 className={desktopGroupLabelClass}>Programma’s</h3>
                  <div className="border-t border-mmBorder w-48 my-2" />
                  <RouterLink
                    to="/program"
                    className={desktopSubmenuLinkClass}
                    onClick={() => setNav(false)}
                  >
                    Defensie-programma
                  </RouterLink>
                  <RouterLink
                    to="/cluster-6-voorbereiding-vrouwen"
                    className={desktopSubmenuLinkClass}
                    onClick={() => setNav(false)}
                  >
                    Cluster 6 Vrouwen
                  </RouterLink>
                  <RouterLink
                    to="/programma-veiligheidsdiensten"
                    className={desktopSubmenuLinkClass}
                    onClick={() => setNav(false)}
                  >
                    Programma Veiligheidsdiensten
                  </RouterLink>
                  <RouterLink
                    to="/pricing"
                    className={desktopSubmenuLinkClass}
                    onClick={() => setNav(false)}
                  >
                    Aanbod & prijzen
                  </RouterLink>
                </div>

                <div>
                  <h3 className={desktopGroupLabelClass}>Kennisbank</h3>
                  <div className="border-t border-mmBorder w-48 my-2" />
                  <RouterLink
                    to="/blogs"
                    className={desktopSubmenuLinkClass}
                    onClick={() => setNav(false)}
                  >
                    Artikelen
                  </RouterLink>
                  <RouterLink
                    to="/resources"
                    className={desktopSubmenuLinkClass}
                    onClick={() => setNav(false)}
                  >
                    Bronnen
                  </RouterLink>
                </div>

                <div>
                  <RouterLink
                    to="/ervaringen"
                    className={desktopLinkClass}
                    onClick={() => setNav(false)}
                  >
                    Ervaringen
                  </RouterLink>
                  <div className="border-t border-mmBorder w-48 my-2" />
                </div>

                <div>
                  <RouterLink
                    to="/about"
                    className={desktopLinkClass}
                    onClick={() => setNav(false)}
                  >
                    Over ons
                  </RouterLink>
                  <div className="border-t border-mmBorder w-48 my-2" />
                </div>

                <div>
                  <RouterLink
                    to="/contact"
                    className={desktopLinkClass}
                    onClick={() => setNav(false)}
                  >
                    Contact
                  </RouterLink>
                  <div className="border-t border-mmBorder w-40 my-2" />
                </div>
              </div>

              {/* Column 3 */}
              <div className="flex items-center justify-center h-full">
                <img
                  src="/img/MissionMovement-yellow.png"
                  alt="Mission Movement"
                  className="w-[420px] max-w-full h-auto object-contain"
                  width={8984}
                  height={5220}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Nav;
